import { Flex } from 'antd';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { useEffect, useMemo, useState } from 'react';

import { Spin } from '../Ant';
import { useTranslation } from 'react-i18next';
import CustomerCard from './customerCard';
import SortSelector from '../sortPopover/sortSelector';
import { usePage } from '../../context/PageContext';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useFetchCustomers } from '../../hooks/useFetchCustomers';
import { useInView } from 'react-intersection-observer';
import NoData from '../dashboard/noData';
import { ProfileUserEmpty } from '../icons';
import { ICustomer } from '../../interface/customer';
import { useQueryClient } from '@tanstack/react-query';
import { IFilterOption } from '../../interface/transaction';
import { ICustomerType } from '../../constants/customer';
import { MY_OWN_FILTER, ROUTES } from '../../constants/constants';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useEscapeToGoBack } from '../../hooks/useEscapeToGoBack';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsPage } from '../../interface/analytics';
import { useSearchParams } from 'react-router-dom';
import { useManageMatterRequestContext } from '../../context/ManageMatterRequest';
import { IMatterRequest } from '../../interface/matterRequest';
import { IActiveSortState, ISortOption } from '../../interface/common';
import SkeletonCardList from '../common/skeleton/skeletonCardList';
import { useListViewPreference } from '../../context/ListViewPreferenceContext';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';
import { buildPrincipalCustomerFields } from './principalCustomer';
import { useOwnerFilter } from '../../hooks/useOwnerFilter';
import { useUser } from '../../context/UserContext';
const CustomersListing = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { setTitle } = usePage();
  const { clear, push, goBack, goBackWithSelection } = useNavigationStack();
  const { trackPage } = useAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();
  const { matterRequest, updateManageMatterRequest } = useManageMatterRequestContext();
  const { getActiveFavorite, setActiveFavorite } = useListViewPreference();
  const { user } = useUser();
  const ownerFilter = useOwnerFilter({
    isMyOwnDefault: true,
    restoredValue: searchParams.get('owner'),
  });
  const isFromMatterRequest = searchParams.get('isFromMatterRequest') === 'true';
  const selectedCustomerId = searchParams.get('selectedCustomerId') ?? null;
  const [activeSort, setActiveSort] = useState<IActiveSortState>({
    field: 'name',
    direction: 'ASC',
  });
  const customerFilterOptions: IFilterOption[] = [
    {
      label: t('customer:company'),
      value: ICustomerType.COMPANY,
    },
    {
      label: t('customer:privatePerson'),
      value: ICustomerType.PRIVATE_PERSON,
    },
  ];

  const customerSortOptions: ISortOption[] = useMemo(
    () => [
      { field: 'name', label: t('customer:name'), type: 'text', defaultDirection: 'ASC' },
      {
        field: 'customerNumber',
        label: t('customer:customerNumber'),
        type: 'number',
        defaultDirection: 'ASC',
      },
    ],
    [t],
  );

  const initialSearch = searchParams.get('search') || '';
  const initialFilterValue = searchParams.get('filter');
  const initialFilter = customerFilterOptions.find((opt) => opt.value === initialFilterValue);
  const initialFavorite = searchParams.get('favorite')
    ? searchParams.get('favorite') === 'true'
    : getActiveFavorite('customers');
  const [searchValue, setSearchValue] = useState<string>(initialSearch);
  const [isFavorite, setIsFavorite] = useState<boolean>(initialFavorite);
  const handleFavoriteToggle = (value: boolean) => {
    setIsFavorite(value);
    setActiveFavorite('customers', value);
  };
  const [selectedFilter, setSelectedFilter] = useState<IFilterOption | undefined>(initialFilter);
  const {
    data: customerList,
    isLoading: isCustomersLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFetchCustomers({
    sortBy: [`${activeSort.field}:${activeSort.direction}`],
    limit: 50,
    search: searchValue,
    extraFilters: {
      type: selectedFilter?.value,
      responsibleUserId: ownerFilter.isMyOwn ? user?.id : undefined,
    },
    isFavorite,
    withFavoriteInformation: true,
    fields: [
      'name',
      'homeCountry',
      'primaryPhone',
      'type',
      'hasImportantNotes',
      'hasNotes',
      'companyNumber',
      'privatePersonNumber',
    ],
  });

  useEffect(() => {
    trackPage(AnalyticsPage.CUSTOMER_LISTING);
  }, [trackPage]);

  useEffect(() => {
    setSearchParams({
      search: searchValue,
      filter: selectedFilter?.value || '',
      favorite: isFavorite.toString(),
      owner: ownerFilter.value,
      isFromMatterRequest: isFromMatterRequest ? 'true' : '',
      selectedCustomerId: selectedCustomerId || '',
    });
  }, [searchValue, selectedFilter, isFavorite, ownerFilter.value]);

  const { ref, inView } = useInView({
    threshold: 0.5,
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && customerList?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  const handleHome = () => {
    clear();
    push(ROUTES.DASHBOARD);
  };
  const handleBack = () => {
    goBack();
  };
  // Only as a picker is there a screen to go back to.
  useEscapeToGoBack(handleBack, isFromMatterRequest);
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        title={t('common:features.customers')}
        showMenu={!isFromMatterRequest}
        showSettings={!isFromMatterRequest}
        handleHome={isFromMatterRequest ? undefined : handleHome}
        handleBack={isFromMatterRequest ? handleBack : undefined}
      />,
    );
  }, []);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin />;
    }
    return null;
  };
  const handleSelectCustomer = (customer: ICustomer) => {
    if (isFromMatterRequest) {
      const principalFields = buildPrincipalCustomerFields(customer);
      if (principalFields) {
        updateManageMatterRequest({
          ...matterRequest,
          ...principalFields,
        } as unknown as IMatterRequest);
      }
      goBackWithSelection();
      return;
    }
    push(`${ROUTES.CUSTOMER_LISTING}/${customer.id}`);
  };
  return (
    <CollapsibleListLayout
      testId="customers-listing"
      header={
        <SearchInputWithFilters
          searchTitle={t('customer:search')}
          filterTitle={t('customer:filter.label')}
          ownerOptions={ownerFilter.options}
          ownerTitle={ownerFilter.title}
          setSelectedOwner={ownerFilter.select}
          radioTitle={t('customer:type')}
          radioOptions={customerFilterOptions}
          handleRefresh={() => {
            queryClient.removeQueries({ queryKey: ['tasks-list'] });
            return refetch();
          }}
          selectedFilter={[
            ...ownerFilter.selectedOptions,
            ...(selectedFilter ? [selectedFilter] : []),
          ]}
          setSelectedFilter={(val) => setSelectedFilter(val)}
          clearFilter={(val?: string) => {
            if (val === MY_OWN_FILTER) {
              ownerFilter.reset();
              return;
            }
            if (!val) ownerFilter.reset();
            setSelectedFilter(undefined);
          }}
          searchValue={searchValue}
          handleSearch={(val: string) => {
            setSearchValue(val);
          }}
          isFavouriteFilterActive={isFavorite}
          handleFavouriteToggle={handleFavoriteToggle}
        >
          <SortSelector
            modalLabel={t('common:sorting.title')}
            options={customerSortOptions}
            activeSort={activeSort}
            onApply={(sort) => setActiveSort(sort)}
          />
        </SearchInputWithFilters>
      }
    >
      {customerList &&
        customerList.length > 0 &&
        customerList.map((customer: ICustomer) => {
          return (
            <CustomerCard
              data-testid={`customer-card-${customer?.name}`}
              key={customer.id}
              customer={customer}
              isSelected={selectedCustomerId === customer.id}
              onClick={() => handleSelectCustomer(customer)}
            />
          );
        })}
      {hasNextPage && <div ref={ref} className="loading-indicator" />}
      {(!customerList || (Object.entries(customerList).length === 0 && !isCustomersLoading)) && (
        <Flex className="flex-grow" justify="center" align="center">
          <NoData
            header={t('customer:noCustomers')}
            subHeader={t('common:tryChangingSearchCriteria')}
            icon={<ProfileUserEmpty />}
          />
        </Flex>
      )}
      {isCustomersLoading ? (
        <SkeletonCardList rows={[1, 1]} withSubcontentOnFirstRow />
      ) : (
        renderLoader()
      )}
    </CollapsibleListLayout>
  );
};

export default CustomersListing;
